import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY = 'flexleague_calendar_time_preferences'
const DEFAULT_START_HOUR = 17
const DEFAULT_END_HOUR = 23

/**
 * Custom hook for managing weekly calendar time range preferences.
 * Stores preferences in localStorage for persistence across sessions.
 * 
 * @returns {Object} Time preferences and control functions
 */
export function useCalendarTimePreferences() {
  const [startHour, setStartHour] = useState(DEFAULT_START_HOUR)
  const [endHour, setEndHour] = useState(DEFAULT_END_HOUR)

  // Load preferences from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const preferences = JSON.parse(stored)
        if (typeof preferences.startHour === 'number' && 
            typeof preferences.endHour === 'number' &&
            preferences.startHour >= 0 && 
            preferences.startHour <= 23 &&
            preferences.endHour >= 0 && 
            preferences.endHour <= 23 &&
            preferences.startHour < preferences.endHour) {
          setStartHour(preferences.startHour)
          setEndHour(preferences.endHour)
        }
      }
    } catch (error) {
      // If localStorage fails, use defaults
      console.warn('Failed to load calendar time preferences:', error)
    }
  }, [])

  // Save preferences to localStorage
  const savePreferences = useCallback((newStartHour, newEndHour) => {
    try {
      // Validate inputs
      if (typeof newStartHour !== 'number' || 
          typeof newEndHour !== 'number' ||
          newStartHour < 0 || 
          newStartHour > 23 ||
          newEndHour < 0 || 
          newEndHour > 23 ||
          newStartHour >= newEndHour) {
        console.error('Invalid time range:', newStartHour, newEndHour)
        return false
      }

      const preferences = {
        startHour: newStartHour,
        endHour: newEndHour,
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences))
      setStartHour(newStartHour)
      setEndHour(newEndHour)
      return true
    } catch (error) {
      console.error('Failed to save calendar time preferences:', error)
      return false
    }
  }, [])

  // Reset to default preferences
  const resetPreferences = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY)
      setStartHour(DEFAULT_START_HOUR)
      setEndHour(DEFAULT_END_HOUR)
      return true
    } catch (error) {
      console.error('Failed to reset calendar time preferences:', error)
      return false
    }
  }, [])

  return {
    startHour,
    endHour,
    savePreferences,
    resetPreferences,
    isDefault: startHour === DEFAULT_START_HOUR && endHour === DEFAULT_END_HOUR,
  }
}
